import Colors from "@constants/Colors"
import { SimplePaletteColorOptions } from "@mui/material"

export const lightColorPalette: Record<string, SimplePaletteColorOptions> = {
    tableRow: {
        main: Colors.NEUTRAL_WHITE,
    },
    tableHead: {
        main: "#f9f9f9",
    },
    border: {
        main: "#252525",
    },
    primary: {
        main: Colors.PRIMARY,
    },
    secondary: {
        main: Colors.NEUTRAL_WHITE,
        contrastText: Colors.WHITE,
    },
    info: {
        main: Colors.NEUTRAL_WHITE,
        contrastText: Colors.PRIMARY,
    },
    muted: {
        main: '#B0BEC5', // Gray color for light mode
        contrastText: Colors.NEUTRAL_BLACK,
    },
    neutralWhite: {
        main: Colors.FLOATING_BAR_TEXT,
        contrastText: Colors.PRIMARY, // text should be primary
    },
    darkGrey: {
        main: Colors.NEUTRAL_DARK_GRAY,
    },
    lightGrey: {
        main: '#939393',
    },
    neutralLightGrey: {
        main: '#f9f9f9',
    },
    dashboard: {
        main: `#f9f9f9`,
    },
    disableFormBlack: {
        main: '#F9F9F9',
    },
    primaryBlackPurple: {
        main: '#3c2de1',
    },
    neutralBlack: {
        main: '#F9F9F9',
    },
    neutralGray: {
        main: '#706d85',
    },
    errorRed: {
        main: '#e45858',
    },
    othersFree: {
        main: '#bb6bd9',
    },
    warning: {
        main: '#f2994a',
    },
    warningOrange: {
        main: '#f2994a',
    },
    othersGold: {
        main: '#bdae40',
    },
    secondaryRed: {
        main: '#d34059',
    },
    othersBlue: {
        main: '#2f80ed',
    },
    primaryWhitePurple: {
        main: '#faf6fd',
    },
    primaryPurple10: {
        main: '#F7F6FF',
    },
    backgroundPilsurface: {
        main: 'rgba(60, 45, 225, 0.3)',
    },
    colorNeutralDashboard: {
        main: 'rgba(34, 36, 38, 1)',
    },
    borderBorderSecondary: {
        main: '#4239b0',
    },
    neutralDarkGray2: {
        main: '#706d85',
    },
    colorSecondary400: {
        main: '#706d85',
    },
    neutralDarkGray: {
        main: '#f2f1ff',
    },
    textSecondary: {
        main: '#cbcbcb',
    },
    gray: {
        main: '#939393',
    },
    field: {
        main: '#282C30',
    },
    textLink: {
        main: Colors.PRIMARY,
    },
    transparentPurple: {
        main: 'rgba(60, 45, 225, 0.3)',
    },
    primaryPurple: {
        main: '#4239b0',
    },
    backgroundBar: {
        main: '#f9f9f9',
    },
    backgroundPrimary: {
        main: 'rgba(255, 255, 255, 0.25)',
    },
    successGreen: {
        main: 'rgba(141, 214, 144, 1)',
    },
    yellowYellow: {
        main: 'rgba(242, 215, 74, 1)',
    },
    green: {
        main: 'rgba(17, 153, 142, 1)',
    },
    textPrimary: {
        main: 'rgba(0, 0, 0, 1)',
    },
    primaryPurple30: {
        main: 'rgba(242, 241, 255, 1)',
    },
    otherSuccess: {
        main: 'rgba(141, 214, 144, 1)',
    },
    textInvert: {
        main: 'rgba(12, 13, 24, 1)',
    },
    colorSecondary50: {
        main: 'rgba(203, 203, 203, 1)'
    },
    purpose: {
        main: 'rgba(17, 137, 153, 1)',
    },

    // ===============================
    // MIGRATION: Missing Colors Added 
    // ===============================

    // Interactive State Colors
    focusPurple: {
        main: '#5447DE', // Input focus states
    },
    hoverPurple: {
        main: '#372E99', // Button and element hover states
    },
    disabledGray: {
        main: '#424242', // Disabled form elements
    },

    // Surface & Layout Colors
    floatingText: {
        main: '#242333', // Overlay text color
    },
    modalBackdrop: {
        main: 'rgba(0, 0, 0, 0.7)', // Modal background overlay
    },
    overlayLight: {
        main: 'rgba(255, 255, 255, 0.3)', // Semi-transparent overlays
    },

    // Status Indicator Colors (Job Application States)
    statusContact: {
        main: '#CBCBCB', // Contact state
    },
    statusInterview: {
        main: '#2F80ED', // Interview state (blue)
    },
    statusTrial: {
        main: '#F2994A', // Trial state (orange) 
    },
    statusProposal: {
        main: '#118999', // Proposal state (dark green)
    },
    statusProbation: {
        main: '#5447DE', // Probation state (purple)
    },

    // Editor-specific Colors
    editorTeal: {
        main: '#118999', // Text editor link colors
    },
    editorPink: {
        main: '#EA8080', // Text editor error highlights
    },

    // Gradient Component Colors  
    gradientTeal: {
        main: '#11998E', // Avatar gradient start
    },
    gradientGreen: {
        main: '#38EF7D', // Avatar gradient end
    },
    gradientYellow: {
        main: '#FDC830', // Warning gradient
    },
    gradientOrange: {
        main: '#F37335', // Success gradient
    },

    // Dark Mode Background Variants (used in light mode for contrast)
    bgPurpleDark: {
        main: '#262738', // Dark purple backgrounds
    },
    bgGreenDark: {
        main: '#1F3838', // Dark green backgrounds
    },
    bgYellowDark: {
        main: '#45422C', // Dark yellow backgrounds
    },
    bgPinkDark: {
        main: '#40292F', // Dark pink backgrounds  
    },
    bgBlueDark: {
        main: '#243448', // Dark blue backgrounds
    },
}

export const darkColorPalette: Record<string, SimplePaletteColorOptions> = {
    tableRow: {
        main: "#0C0D18",
    },
    tableHead: {
        main: "#000000",
    },
    border: {
        main: "#7C798B",
    },
    primary: {
        main: Colors.PRIMARY,
    },
    secondary: {
        main: Colors.NEUTRAL_WHITE,
        contrastText: Colors.NEUTRAL_BLACK,
    },
    info: {
        main: Colors.NEUTRAL_BLACK,
        contrastText: Colors.PRIMARY,
    },
    muted: {
        main: Colors.NEUTRAL_WHITE, // White color for dark mode
        contrastText: Colors.NEUTRAL_BLACK,
    },
    darkGrey: {
        main: Colors.NEUTRAL_DARK_GRAY,
    },
    lightGrey: {
        main: '#cbcbcb',
    },
    neutralLightGrey: {
        main: '#f9f9f9',
    },
    dashboard: {
        main: `#222426`,
    },
    disableFormBlack: {
        main: '#282c30',
    },
    primaryBlackPurple: {
        main: '#3c2de1',
    },
    neutralBlack: {
        main: '#000000',
    },
    neutralGray: {
        main: '#cbcbcb',
    },
    errorRed: {
        main: '#e45858',
    },
    othersFree: {
        main: '#bb6bd9',
    },
    warning: {
        main: '#f2994a',
    },
    warningOrange: {
        main: '#f2994a',
    },
    othersGold: {
        main: '#bdae40',
    },
    secondaryRed: {
        main: '#d34059',
    },
    othersBlue: {
        main: '#2f80ed',
    },
    transparentPurple: {
        main: 'rgba(60, 45, 225, 0.3)',
    },
    primaryWhitePurple: {
        main: '#faf6fd',
    },
    colorSecondary400: {
        main: '#706d85',
    },
    neutralDarkGray: {
        main: '#7c798b',
    },
    neutralDarkGray2: {
        main: '#706d85',
    },
    textSecondary: {
        main: '#cbcbcb',
    },
    backgroundPilsurface: {
        main: 'rgba(60, 45, 225, 0.3)',
    },
    neutralWhite: {
        main: '#fff',
        contrastText: Colors.PRIMARY, // text should be primary
    },
    gray: {
        main: '#c0c0c0',
    },
    borderBorderSecondary: {
        main: '#7c798b',
    },
    primaryPurple: {
        main: '#4239b0',
    },
    backgroundBar: {
        main: '#0c0d18',
    },
    textLink: {
        main: '#fff',
    },
    colorNeutralDashboard: {
        main: 'rgba(34, 36, 38, 1)',
    },
    backgroundPrimary: {
        main: 'rgba(0, 0, 0, 0.25)',
    },
    successGreen: {
        main: 'rgba(141, 214, 144, 1)',
    },
    yellowYellow: {
        main: 'rgba(242, 215, 74, 1)',
    },
    green: {
        main: 'rgba(17, 153, 142, 1)',
    },
    textPrimary: {
        main: 'rgba(255, 255, 255, 1)',
    },
    primaryPurple10: {
        main: '#F7F6FF',
    },
    primaryPurple30: {
        main: 'rgba(242, 241, 255, 1)',
    },
    otherSuccess: {
        main: 'rgba(141, 214, 144, 1)',
    },
    textInvert: {
        main: 'rgba(12, 13, 24, 1)',
    },
    colorSecondary50: {
        main: 'rgba(203, 203, 203, 1)'
    },
    field: {
        main: '#282C30',
    },
    purpose: {
        main: 'rgba(17, 137, 153, 1)',
    },

    // ===============================
    // MIGRATION: Missing Colors Added (Dark Mode)
    // ===============================

    // Interactive State Colors (Dark Mode)
    focusPurple: {
        main: '#6B5FE8', // Brighter purple for dark mode focus states
    },
    hoverPurple: {
        main: '#4239B0', // Primary purple for hover in dark mode
    },
    disabledGray: {
        main: '#666666', // Lighter gray for disabled elements in dark mode
    },

    // Surface & Layout Colors (Dark Mode)
    floatingText: {
        main: '#FFFFFF', // White text for dark mode overlays
    },
    modalBackdrop: {
        main: 'rgba(0, 0, 0, 0.8)', // Darker backdrop for dark mode
    },
    overlayLight: {
        main: 'rgba(255, 255, 255, 0.1)', // Very subtle white overlay for dark mode
    },

    // Status Indicator Colors (Dark Mode - keeping semantic meaning)
    statusContact: {
        main: '#CBCBCB', // Same - neutral gray works in both modes
    },
    statusInterview: {
        main: '#4DA6FF', // Brighter blue for dark mode
    },
    statusTrial: {
        main: '#FFB84D', // Brighter orange for dark mode
    },
    statusProposal: {
        main: '#14B8A6', // Brighter teal for dark mode
    },
    statusProbation: {
        main: '#6B5FE8', // Brighter purple for dark mode
    },

    // Editor-specific Colors (Dark Mode)
    editorTeal: {
        main: '#22D3EE', // Cyan for dark mode editor links
    },
    editorPink: {
        main: '#F87171', // Brighter pink for dark mode error highlights
    },

    // Gradient Component Colors (Dark Mode)
    gradientTeal: {
        main: '#14B8A6', // Brighter teal for dark mode gradients
    },
    gradientGreen: {
        main: '#4ADE80', // Brighter green for dark mode gradients
    },
    gradientYellow: {
        main: '#FACC15', // Brighter yellow for dark mode gradients
    },
    gradientOrange: {
        main: '#FB923C', // Brighter orange for dark mode gradients
    },

    // Dark Mode Background Variants (actually used as backgrounds in dark mode)
    bgPurpleDark: {
        main: '#1E1B4B', // Deep purple for dark mode backgrounds
    },
    bgGreenDark: {
        main: '#134E4A', // Deep green for dark mode backgrounds
    },
    bgYellowDark: {
        main: '#451A03', // Deep brown for dark mode backgrounds
    },
    bgPinkDark: {
        main: '#4C1D24', // Deep red for dark mode backgrounds
    },
    bgBlueDark: {
        main: '#0F172A', // Deep blue for dark mode backgrounds
    },
}
